import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import Modal from '../../components/ui/Modal';
import { Calculator, AlertTriangle, Check } from 'lucide-react';
import './RecalculatePricesModal.css';

/**
 * RecalculatePricesModal — bulk "apply my markup to existing products"
 * tool. Lives in Settings → Pricing defaults.
 *
 * Flow:
 *   1. On open: GET /merchants/pricing/recalculate-preview with the
 *      two default filters on (skip products with their own override,
 *      skip products without a Unit Cost).
 *   2. Render a table of candidates with current price, new price,
 *      delta, source of markup. Per-row checkbox + select-all.
 *   3. On Apply: POST /merchants/pricing/recalculate-prices with
 *      productIds[]. Toast the summary. Close.
 */
export default function RecalculatePricesModal({ isOpen, onClose }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [onlyMissingOverride, setOnlyMissingOverride] = useState(true);
  const [onlyWithCost, setOnlyWithCost] = useState(true);
  const [applying, setApplying] = useState(false);

  // Reload preview whenever filters change.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get('/merchants/pricing/recalculate-preview', {
          params: { onlyMissingOverride, onlyWithCost }
        });
        if (!cancelled) {
          setPreview(data);
          // Default-select every row that would actually change.
          setSelectedIds(new Set(
            data.items.filter((it) => it.wouldChange).map((it) => it.productId)
          ));
        }
      } catch (e) {
        if (!cancelled) toast.error('Failed to load preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, onlyMissingOverride, onlyWithCost, toast]);

  // Reset state when modal closes so re-open is clean.
  useEffect(() => {
    if (!isOpen) {
      setPreview(null);
      setSelectedIds(new Set());
      setLoading(true);
    }
  }, [isOpen]);

  const items = preview?.items || [];
  // Two derived buckets: rows that are eligible to apply (cost set,
  // markup set, would change) vs rows that are visible but blocked
  // (cost missing → can't compute). Both are listed so the merchant
  // sees the whole picture instead of mystery-skipped rows.
  const eligibleItems = useMemo(() => items.filter((i) => i.wouldChange), [items]);
  const blockedItems = useMemo(
    () => items.filter((i) => !i.wouldChange && (i.costPrice == null || i.costPrice <= 0)),
    [items]
  );
  const displayItems = useMemo(
    () => [...eligibleItems, ...blockedItems],
    [eligibleItems, blockedItems]
  );

  const toggleAll = () => {
    if (selectedIds.size === eligibleItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligibleItems.map((i) => i.productId)));
    }
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = async () => {
    const productIds = [...selectedIds];
    if (productIds.length === 0) {
      toast.warning('Select at least one product to recalculate');
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post('/merchants/pricing/recalculate-prices', { productIds });
      toast.success(
        `Updated ${data.updated} product${data.updated === 1 ? '' : 's'}${
          data.skipped > 0 ? ` (${data.skipped} skipped)` : ''
        }`
      );
      onClose();
    } catch (e) {
      toast.error('Failed to recalculate prices');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Recalculate existing prices" size="lg">
      <div className="rpm-root">
        <div className="rpm-intro">
          <Calculator size={18} />
          <div>
            <div className="rpm-intro-title">Apply your store-wide markup to existing products</div>
            <div className="rpm-intro-sub">
              Only products that <em>would actually change</em> are pre-selected. Hand-edit your picks before applying. Markup hierarchy still applies — product overrides win, then category, then store default.
            </div>
          </div>
        </div>

        <div className="rpm-filters">
          <label className="rpm-filter">
            <input
              type="checkbox"
              checked={onlyMissingOverride}
              onChange={(e) => setOnlyMissingOverride(e.target.checked)}
            />
            <span>Only products without their own Markup % override</span>
          </label>
          <label className="rpm-filter">
            <input
              type="checkbox"
              checked={onlyWithCost}
              onChange={(e) => setOnlyWithCost(e.target.checked)}
            />
            <span>Only products with a Unit Cost set</span>
          </label>
        </div>

        {loading && <div className="rpm-empty">Loading preview…</div>}

        {!loading && preview && items.length === 0 && (
          <div className="rpm-empty">
            <AlertTriangle size={28} />
            <div className="rpm-empty-title">Nothing to preview</div>
            <div className="rpm-empty-sub">
              No products match the current filters. Try unticking one or set a store-wide markup in Pricing defaults above.
            </div>
          </div>
        )}

        {!loading && eligibleItems.length === 0 && blockedItems.length === 0 && items.length > 0 && (
          <div className="rpm-empty rpm-empty--success">
            <Check size={28} />
            <div className="rpm-empty-title">All set</div>
            <div className="rpm-empty-sub">
              Every product that matched the filters is already at its markup-suggested price.
            </div>
          </div>
        )}

        {!loading && displayItems.length > 0 && (
          <>
            <div className="rpm-summary">
              <span>
                <strong>{eligibleItems.length}</strong> would change
              </span>
              {blockedItems.length > 0 && (
                <span className="rpm-summary-skip">· {blockedItems.length} blocked (no cost set)</span>
              )}
              {preview.skipped.hasOverride > 0 && (
                <span className="rpm-summary-skip">· {preview.skipped.hasOverride} skipped (own override)</span>
              )}
              {preview.skipped.noMarkup > 0 && (
                <span className="rpm-summary-skip">· {preview.skipped.noMarkup} skipped (no markup)</span>
              )}
            </div>

            <div className="rpm-table-wrap">
              <table className="rpm-table">
                <colgroup>
                  <col style={{ width: 40 }} />
                  <col />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 95 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 110 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="rpm-th-check">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === eligibleItems.length && eligibleItems.length > 0}
                        onChange={toggleAll}
                      />
                    </th>
                    <th>Product</th>
                    <th className="rpm-th-num">Cost</th>
                    <th className="rpm-th-num">Current</th>
                    <th className="rpm-th-num">New</th>
                    <th className="rpm-th-num">Δ</th>
                    <th>Markup</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibleItems.map((it) => (
                    <tr key={it.productId} className={selectedIds.has(it.productId) ? 'is-selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(it.productId)}
                          onChange={() => toggleOne(it.productId)}
                        />
                      </td>
                      <td className="rpm-td-name">
                        <div className="rpm-name">{it.name}</div>
                        {it.categoryName && <div className="rpm-cat">{it.categoryName}</div>}
                      </td>
                      <td className="rpm-td-num" data-label="Cost">₦{Number(it.costPrice || 0).toLocaleString()}</td>
                      <td className="rpm-td-num" data-label="Current">₦{Number(it.currentPrice).toLocaleString()}</td>
                      <td className="rpm-td-num rpm-td-new" data-label="New">₦{Number(it.suggestedPrice).toLocaleString()}</td>
                      <td className={`rpm-td-num ${it.delta >= 0 ? 'rpm-delta-up' : 'rpm-delta-down'}`} data-label="Δ">
                        {it.delta >= 0 ? '+' : ''}₦{Math.abs(it.delta).toLocaleString()}
                      </td>
                      <td className="rpm-td-markup" data-label="Markup">
                        <span className="rpm-markup-pct">{it.markupPercent}%</span>
                        <span className="rpm-markup-src">{it.markupSource}</span>
                      </td>
                    </tr>
                  ))}
                  {/* Phase 4.A.6 follow-on — blocked rows (no Unit Cost
                      on the product). Listed for visibility with a
                      disabled checkbox + an "Open product" link so the
                      merchant can fix the missing cost in one tap. */}
                  {blockedItems.map((it) => (
                    <tr key={it.productId} className="rpm-row-blocked">
                      <td>
                        <input type="checkbox" disabled title="Set Unit Cost on the product first" />
                      </td>
                      <td className="rpm-td-name">
                        <div className="rpm-name">{it.name}</div>
                        {it.categoryName && <div className="rpm-cat">{it.categoryName}</div>}
                      </td>
                      <td className="rpm-td-num rpm-td-blocked" data-label="Cost">—</td>
                      <td className="rpm-td-num" data-label="Current">₦{Number(it.currentPrice).toLocaleString()}</td>
                      <td className="rpm-td-num rpm-td-blocked" data-label="New">—</td>
                      <td className="rpm-td-num rpm-td-blocked" data-label="Δ">—</td>
                      <td className="rpm-td-markup" data-label="Fix">
                        <a
                          href={`/products/${it.productId}/edit`}
                          target="_blank"
                          rel="noreferrer"
                          className="rpm-fix-link"
                        >
                          Set cost →
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="rpm-actions">
          <button type="button" className="rpm-btn rpm-btn--ghost" onClick={onClose} disabled={applying}>
            Cancel
          </button>
          <button
            type="button"
            className="rpm-btn rpm-btn--primary"
            onClick={handleApply}
            disabled={applying || selectedIds.size === 0 || loading}
          >
            {applying
              ? 'Applying…'
              : selectedIds.size === 0
                ? 'Apply to selected'
                : `Apply to ${selectedIds.size} product${selectedIds.size === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
